import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { queryKeys } from '@/lib/queryClient'
import { useAuthStore } from '@/stores/authStore'
import type { StoreOrder, StoreOrderStatus } from '@/types/app'

export function useStoreOrders() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  return useQuery({
    queryKey: queryKeys.storeOrders(restaurantId),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('store_orders')
        .select('*, items:store_order_items(*)')
        .eq('restaurant_id', restaurantId)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as StoreOrder[]
    },
    enabled: !!restaurantId,
  })
}

export function useUpdateStoreOrderStatus() {
  const restaurantId = useAuthStore(s => s.restaurant?.id ?? '')
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: StoreOrderStatus }) => {
      const patch: Record<string, unknown> = { status }
      // Payment isn't collected online yet (no Stripe wired up) — marking an
      // order "confirmed" is staff's manual signal that they've accepted the
      // order and, typically, collected payment in person/by phone.
      if (status === 'confirmed') patch.paid_at = new Date().toISOString()
      const { data, error } = await supabase
        .from('store_orders').update(patch).eq('id', id).select().single()
      if (error) throw error
      return data as StoreOrder
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.storeOrders(restaurantId) })
    },
  })
}
